import { useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'

import { Alert, Button, Card, TextArea } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { useAddArticleComment, useArticleComments, useResolveArticleComment } from 'hooks/api/useArticleCovers'
import type { ArticleCommentTarget } from 'services/coversApi'

/**
 * Comments on a piece's words or its cover, on the page itself (Spencer, 2026-11-04: "a way to
 * comment on a cover or overview page directly, instead of just in Claude"). A comment on the cover
 * can name one frame. Resolving keeps the comment, struck from the open list.
 */
export const ArticleComments = ({
  section,
  slug,
  target,
  refs = [],
}: {
  section: string
  slug: string
  target: ArticleCommentTarget
  refs?: { id: string; label: string }[]
}) => {
  const { open: notify } = useSnackbar()
  const { data, error } = useArticleComments(section, slug)
  const add = useAddArticleComment(section, slug)
  const resolve = useResolveArticleComment(section, slug)
  const [body, setBody] = useState('')
  const [ref, setRef] = useState<string | null>(null)
  const label = (id: string | null) => refs.find((r) => r.id === id)?.label ?? null
  const mine = (data?.comments ?? []).filter((c) => c.target === target)
  const open = mine.filter((c) => !c.resolvedAt)
  const closed = mine.length - open.length
  const send = () =>
    add.mutate(
      { target, targetRef: ref, body: body.trim() },
      {
        onSuccess: () => {
          setBody('')
          setRef(null)
        },
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )
  return (
    <Card title={target === 'cover' ? 'Comments on the cover' : 'Comments'} sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        {error ? <Alert severity="error" title="Could not load the comments" description={(error as Error).message} /> : null}
        {open.map((c) => (
          <Box key={c.id} sx={(t) => ({ borderBottom: `1px solid ${t.palette.primary[100]}`, pb: 2 })}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body3" sx={{ fontWeight: 600 }}>
                {c.author ?? 'Unknown'}
              </Typography>
              <Typography variant="body4" color="text.secondary">
                {new Date(c.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </Typography>
              {label(c.targetRef) ? <Chip size="small" variant="outlined" label={label(c.targetRef)} /> : null}
              <Box sx={{ flex: 1 }} />
              <Button variant="secondary" size="small" disabled={resolve.isPending} onClick={() => resolve.mutate(c.id)}>
                Resolve
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
              {c.body}
            </Typography>
          </Box>
        ))}
        {closed ? (
          <Typography variant="body4" color="text.secondary">
            {closed} resolved
          </Typography>
        ) : null}
        {refs.length ? (
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body4" color="text.secondary">
              About
            </Typography>
            <Chip size="small" label="The whole cover" color={ref === null ? 'primary' : 'default'} onClick={() => setRef(null)} />
            {refs.map((r) => (
              <Chip key={r.id} size="small" label={r.label} color={ref === r.id ? 'primary' : 'default'} onClick={() => setRef(r.id)} />
            ))}
          </Stack>
        ) : null}
        <TextArea label="Add a comment" value={body} onChange={setBody} minRows={2} />
        <Box>
          <Button variant="primary" size="small" disableElevation disabled={!body.trim() || add.isPending} onClick={send}>
            {add.isPending ? 'Saving…' : 'Add comment'}
          </Button>
        </Box>
      </Stack>
    </Card>
  )
}
